import { z } from "zod";
import {
  allowedDomainSchema,
  checkIsValidAllowedDomain
} from "../allowedDomain.js";
import {
  baseToolPatchPayloadSchema,
  baseToolPostPayloadSchema
} from "./base.js";
const WebDocumentResearchToolType = "web_document_research";
const webDocumentResearchToolMaxSourceCount = 5;
const webDocumentResearchToolTimeoutMs = 3e4;
const webDocumentResearchToolMaxSourceUrlLength = 2048;
const webDocumentResearchToolMaxAllowedDomainCount = 20;
const webDocumentResearchToolAllowedDomainsSchema = z.array(allowedDomainSchema).min(1, "Add at least one allowed domain.").max(
  webDocumentResearchToolMaxAllowedDomainCount,
  `Add no more than ${webDocumentResearchToolMaxAllowedDomainCount.toString()} allowed domains.`
).transform((domains) => Array.from(new Set(domains)));
const webDocumentResearchToolSettingsSchema = z.object({
  allowed_domains: webDocumentResearchToolAllowedDomainsSchema
});
const webDocumentResearchToolSourceUrlSchema = z.string().max(
  webDocumentResearchToolMaxSourceUrlLength,
  `Source URLs must be ${webDocumentResearchToolMaxSourceUrlLength.toString()} characters or fewer.`
).superRefine((source, ctx) => {
  if (source !== source.trim() || !URL.canParse(source)) {
    ctx.addIssue({ code: "custom", message: "Enter a valid HTTPS URL." });
    return;
  }
  const parsed = new URL(source);
  if (parsed.protocol !== "https:") {
    ctx.addIssue({ code: "custom", message: "Source URLs must use HTTPS." });
  }
  if (parsed.username !== "" || parsed.password !== "") {
    ctx.addIssue({
      code: "custom",
      message: "Source URLs cannot contain credentials."
    });
  }
  if (parsed.port !== "") {
    ctx.addIssue({
      code: "custom",
      message: "Source URLs cannot use a custom port."
    });
  }
  if (!checkIsValidAllowedDomain(parsed.hostname)) {
    ctx.addIssue({
      code: "custom",
      message: "Source URLs must use a valid hostname, not an IP address."
    });
  }
  if (!parsed.pathname.toLowerCase().endsWith(".pdf")) {
    ctx.addIssue({
      code: "custom",
      message: "Source URLs must point to a PDF document."
    });
  }
});
const webDocumentResearchToolInputSchema = z.object({
  prompt: z.string().trim().min(1).describe(
    "A self-contained research request that explains what to find and how to return it."
  ),
  sources: z.array(webDocumentResearchToolSourceUrlSchema).min(1).max(
    webDocumentResearchToolMaxSourceCount,
    `Provide no more than ${webDocumentResearchToolMaxSourceCount.toString()} sources.`
  ).describe(
    "The PDF document URLs to research. Base the response only on these documents."
  )
});
function buildWebDocumentResearchToolParametersSchema(allowedDomains) {
  return {
    type: "object",
    properties: {
      prompt: {
        type: "string",
        description: "A self-contained research request that explains what to find and how to return it."
      },
      sources: {
        type: "array",
        items: { type: "string" },
        description: `One to ${webDocumentResearchToolMaxSourceCount.toString()} HTTPS URLs of PDF documents. Each hostname must exactly match an allowed domain: ${allowedDomains.join(", ")}.`
      }
    },
    required: ["prompt", "sources"],
    additionalProperties: false
  };
}
const webDocumentResearchToolPostPayloadSchema = baseToolPostPayloadSchema.extend({
  type: z.literal(WebDocumentResearchToolType),
  settings: webDocumentResearchToolSettingsSchema
});
const webDocumentResearchToolPatchPayloadSchema = baseToolPatchPayloadSchema.extend({
  type: z.literal(WebDocumentResearchToolType),
  settings: webDocumentResearchToolSettingsSchema.optional()
});
export {
  WebDocumentResearchToolType,
  buildWebDocumentResearchToolParametersSchema,
  webDocumentResearchToolAllowedDomainsSchema,
  webDocumentResearchToolInputSchema,
  webDocumentResearchToolMaxAllowedDomainCount,
  webDocumentResearchToolMaxSourceCount,
  webDocumentResearchToolMaxSourceUrlLength,
  webDocumentResearchToolPatchPayloadSchema,
  webDocumentResearchToolPostPayloadSchema,
  webDocumentResearchToolSettingsSchema,
  webDocumentResearchToolTimeoutMs
};
//# sourceMappingURL=webDocumentResearch.js.map
